// Engagement tab — the 7 drop-off / re-engagement email templates rendered
// in their exact HTML form, plus per-template send analytics from the Stax
// product's labs_lifecycle_email_outreach_log AND from our own ass.engagement_sends.
//
// Includes: pre-send disengagement cohort distributions, live sender stats,
// recent sends list, and click counts (when Resend webhook is wired).

import { useMemo, useState } from 'react';
import {
  DROPOFF_STAGE_IDS,
  DROPOFF_STAGES,
  PERF_CHART_PLACEHOLDER,
  RECENT_CHANGES_DEFAULT,
  renderDropoffEmail,
  type DropoffStageId,
} from '@stax/shared';
import {
  useEngagement,
  useEngagementRecent,
  useEngagementRecentAll,
  useDisengagement,
} from '../lib/queries';

const PREVIEW_FIRST_NAME = 'Alex';
const PREVIEW_REFERRAL_CODE = 'STAX-7K2N-9X3M';
const PREVIEW_RETURN = '+6.4%';
const PREVIEW_RANGE = 'this week';

const ACTIVITY_PAGE_SIZE = 50;

export function Engagement() {
  const [stageId, setStageId] = useState<DropoffStageId>('approved-no-signup');
  const [activityPage, setActivityPage] = useState(0); // 0-indexed; page 0 = rows 1–50
  const { data: engagement, isLoading: engagementLoading } = useEngagement();
  const { data: recent } = useEngagementRecent(stageId, 20);
  const { data: cohort } = useDisengagement(stageId);
  const { data: recentAll, isFetching: recentAllFetching } = useEngagementRecentAll(
    activityPage * ACTIVITY_PAGE_SIZE,
    ACTIVITY_PAGE_SIZE,
  );

  const activityRows = recentAll?.rows ?? [];
  const activityTotal = recentAll?.total ?? 0;
  const activityFirst = activityRows.length === 0 ? 0 : activityPage * ACTIVITY_PAGE_SIZE + 1;
  const activityLast = activityPage * ACTIVITY_PAGE_SIZE + activityRows.length;
  const activityHasNext = activityLast < activityTotal;
  const activityHasPrev = activityPage > 0;

  const stage = DROPOFF_STAGES[stageId];
  const stats = engagement?.analytics?.[stageId];

  const rendered = useMemo(
    () =>
      renderDropoffEmail(stageId, {
        firstName: PREVIEW_FIRST_NAME,
        recentChanges: stageId === 'churn-reengage' ? RECENT_CHANGES_DEFAULT : undefined,
        chartImageSrc: stageId === 'performance-snapshot' ? PERF_CHART_PLACEHOLDER : undefined,
        summaryReturn: stageId === 'performance-snapshot' ? PREVIEW_RETURN : undefined,
        summaryRange: stageId === 'performance-snapshot' ? PREVIEW_RANGE : undefined,
        referralCode: stageId === 'signed-up-no-login' ? PREVIEW_REFERRAL_CODE : undefined,
      }),
    [stageId],
  );

  const liveSent = stats?.ass_sent_count ?? 0;
  const liveFailed = stats?.ass_failed_count ?? 0;
  const liveDry = stats?.ass_dry_count ?? 0;
  const liveClicks = stats?.ass_total_clicks ?? 0;
  const liveLastSentAt = stats?.ass_last_sent_at ?? null;

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section C</span>
          <span>Engagement</span>
          <span>{DROPOFF_STAGE_IDS.length} re-engagement emails</span>
        </div>
        <h1 className="page__title">Engagement</h1>
        <p className="page__deck">
          Seven drop-off emails imported from <code>StaxQuestionnaire/.../dropoffEmails.ts</code>.
          Cohort distributions show the disengaged universe today; send analytics show what's gone out.
        </p>
      </header>

      {/* Cross-stage activity feed — answers "what went out and to whom" at
          a glance. Refreshes every 60s. Each row links its stage label back
          to the per-stage detail below. Paginated 50/page so the operator
          can scroll back through the full history. */}
      <section className="engagement__activity">
        <header className="engagement__activity-head">
          <h2 className="engagement__activity-title">Recent engagement sends</h2>
          <div className="engagement__activity-controls">
            <span className="engagement__activity-sub">
              {activityTotal > 0
                ? `${activityFirst.toLocaleString()}–${activityLast.toLocaleString()} of ${activityTotal.toLocaleString()}`
                : 'No sends yet — the daily 13:00 UTC cron will populate this.'}
              {recentAllFetching && activityRows.length > 0 ? ' · refreshing…' : ''}
            </span>
            {activityTotal > ACTIVITY_PAGE_SIZE && (
              <div className="engagement__activity-pager">
                <button
                  type="button"
                  className="engagement__activity-pager-btn"
                  disabled={!activityHasPrev}
                  onClick={() => setActivityPage((p) => Math.max(0, p - 1))}
                >
                  ← Newer
                </button>
                <button
                  type="button"
                  className="engagement__activity-pager-btn"
                  disabled={!activityHasNext}
                  onClick={() => setActivityPage((p) => p + 1)}
                >
                  Older →
                </button>
              </div>
            )}
          </div>
        </header>
        {activityRows.length > 0 && (
          <table className="engagement__activity-table">
            <thead>
              <tr>
                <th>When (UTC)</th>
                <th>Stage</th>
                <th>To</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {activityRows.map((r) => {
                const ts = r.sent_at ?? r.created_at;
                return (
                  <tr key={r.id} className={`engagement__activity-row engagement__activity-row--${r.status}`}>
                    <td className="engagement__activity-when">
                      {ts
                        ? new Date(ts).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                            timeZone: 'UTC',
                          })
                        : '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="engagement__activity-stage-btn"
                        onClick={() => setStageId(r.stage_id as DropoffStageId)}
                      >
                        {DROPOFF_STAGES[r.stage_id as DropoffStageId]?.title ?? r.stage_id}
                      </button>
                    </td>
                    <td className="engagement__activity-to">{r.recipient_email}</td>
                    <td>
                      <span className={`engagement__activity-status engagement__activity-status--${r.status}`}>
                        {r.status === 'suppressed_dry_run' ? 'dry-run' : r.status}
                      </span>
                      {r.click_count > 0 && (
                        <span className="engagement__activity-clicks"> · {r.click_count} click{r.click_count === 1 ? '' : 's'}</span>
                      )}
                      {r.error && (
                        <span className="engagement__activity-error" title={r.error}> · err</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <nav className="engagement__strip" aria-label="Email stages">
        {DROPOFF_STAGE_IDS.map((id, i) => {
          const s = DROPOFF_STAGES[id];
          const sent = engagement?.analytics?.[id]?.ass_sent_count ?? 0;
          const active = id === stageId;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setStageId(id)}
              className={`engagement__tab ${active ? 'engagement__tab--active' : ''}`}
            >
              <span className="engagement__tab-num">{String(i + 1).padStart(2, '0')}</span>
              <span className="engagement__tab-kind">{s.kind}</span>
              <span className="engagement__tab-title">{s.title}</span>
              <span className="engagement__tab-stat">{sent} sent</span>
            </button>
          );
        })}
      </nav>

      <section className="engagement__detail">
        <header className="engagement__detail-head">
          <div>
            <span className="engagement__detail-kind">{stage.kind}</span>
            <h2 className="engagement__detail-title">{stage.title}</h2>
            <p className="engagement__detail-sub">{stage.triggerSummary}</p>
          </div>
          <dl className="engagement__meta">
            <div>
              <dt>Default delay</dt>
              <dd>{stage.defaultDelay}</dd>
            </div>
            <div>
              <dt>Subject</dt>
              <dd className="engagement__meta-subject">{rendered.subject}</dd>
            </div>
            <div>
              <dt>Sent (lifetime)</dt>
              <dd className="engagement__meta-stat">
                {engagementLoading ? '…' : liveSent.toLocaleString()}
              </dd>
            </div>
            <div>
              <dt>Last sent</dt>
              <dd>
                {liveLastSentAt
                  ? new Date(liveLastSentAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })
                  : '—'}
              </dd>
            </div>
            <div>
              <dt>Dry-run / failed</dt>
              <dd className="engagement__meta-secondary">
                <span>{liveDry.toLocaleString()} dry</span>
                {' · '}
                <span className={liveFailed > 0 ? 'engagement__failed' : ''}>{liveFailed.toLocaleString()} failed</span>
              </dd>
            </div>
            <div>
              <dt>Click-through</dt>
              <dd className="engagement__meta-clicks">
                {liveClicks > 0 ? (
                  <span className="engagement__clicks-yes">
                    {liveClicks.toLocaleString()} click{liveClicks === 1 ? '' : 's'}
                  </span>
                ) : (
                  <>
                    <span className="engagement__not-wired">not yet</span>
                    <span className="engagement__not-wired-note">
                      {engagement?.clicks_status === 'wired' ? 'no clicks for this stage' : 'webhook unwired'}
                    </span>
                  </>
                )}
              </dd>
            </div>
          </dl>
        </header>

        {/* Pre-send disengagement cohort */}
        <section className="cohort">
          <h3 className="cohort__title">Disengaged cohort — right now, before any sends</h3>
          {!cohort ? (
            <p className="cohort__empty">Loading distribution…</p>
          ) : cohort.cohort_count === 0 ? (
            <p className="cohort__empty">
              No users currently match this stage. {stageId === 'performance-snapshot' && '(Recurring — runs weekly against active deployments.)'}
            </p>
          ) : (
            <>
              <div className="cohort__summary">
                <div>
                  <span className="cohort__num">{cohort.cohort_count.toLocaleString()}</span>
                  <span className="cohort__label">in cohort</span>
                </div>
                <div>
                  <span className="cohort__num">{Math.round(cohort.median_days)}</span>
                  <span className="cohort__label">median days disengaged</span>
                </div>
                <div>
                  <span className="cohort__num">{Math.round(cohort.p90_days)}</span>
                  <span className="cohort__label">p90 days</span>
                </div>
                <div>
                  <span className="cohort__num">{Math.round(cohort.oldest_days)}</span>
                  <span className="cohort__label">oldest</span>
                </div>
              </div>
              <DistributionBar cohort={cohort} />
            </>
          )}
        </section>

        <div className="engagement__cols">
          <div className="engagement__preview">
            <div className="engagement__preview-bar">
              <span>Inbox preview</span>
              <span className="engagement__preview-from">
                from hello@updates.staxlabs.org
              </span>
            </div>
            <iframe
              key={stageId}
              title={`Email preview: ${stage.title}`}
              srcDoc={rendered.html}
              className="engagement__iframe"
              sandbox=""
            />
          </div>

          <aside className="engagement__sidebar">
            <h3 className="engagement__side-head">Recent sends</h3>
            {!recent || recent.length === 0 ? (
              <p className="engagement__side-empty">
                No sends yet for this stage. Flip <code>engagement_send_enabled</code> in Settings to start.
              </p>
            ) : (
              <ul className="recent-sends">
                {recent.map((r) => (
                  <li key={r.id} className={`recent-send recent-send--${r.status}`}>
                    <div className="recent-send__head">
                      <span className="recent-send__email">{r.recipient_email}</span>
                      <span className="recent-send__status">{r.status}</span>
                    </div>
                    <div className="recent-send__meta">
                      <span>
                        {(r.sent_at ?? r.created_at)
                          ? new Date(r.sent_at ?? r.created_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : '—'}
                      </span>
                      {r.click_count > 0 && (
                        <span className="recent-send__clicks">{r.click_count} click{r.click_count === 1 ? '' : 's'}</span>
                      )}
                      {r.error && <span className="recent-send__error" title={r.error}>err</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="engagement__side-head">Plain-text fallback</h3>
            <pre className="engagement__plaintext">{rendered.text}</pre>
          </aside>
        </div>
      </section>

      <footer className="engagement__foot">
        <strong>On click tracking:</strong> {engagement?.clicks_note ?? '—'}
      </footer>
    </div>
  );
}

function DistributionBar({ cohort }: { cohort: { bucket_0_7: number; bucket_8_14: number; bucket_15_30: number; bucket_31_90: number; bucket_91_plus: number; cohort_count: number } }) {
  const total = cohort.cohort_count || 1;
  const buckets: Array<{ label: string; count: number }> = [
    { label: '0–7d', count: cohort.bucket_0_7 },
    { label: '8–14d', count: cohort.bucket_8_14 },
    { label: '15–30d', count: cohort.bucket_15_30 },
    { label: '31–90d', count: cohort.bucket_31_90 },
    { label: '91+d', count: cohort.bucket_91_plus },
  ];
  return (
    <div className="distrib">
      <div className="distrib__bar">
        {buckets.map((b, i) => {
          const pct = (b.count / total) * 100;
          return (
            <span
              key={b.label}
              className={`distrib__seg distrib__seg--${i}`}
              style={{ width: `${pct}%` }}
              title={`${b.label}: ${b.count} (${Math.round(pct)}%)`}
            />
          );
        })}
      </div>
      <div className="distrib__legend">
        {buckets.map((b) => (
          <div key={b.label} className="distrib__item">
            <span className="distrib__count">{b.count.toLocaleString()}</span>
            <span className="distrib__range">{b.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
